import React, { useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, Lightbulb, Heart, AlertCircle } from 'lucide-react';
import DiscussionNotes from './DiscussionNotes';

/**
 * InsightsPanel - Displays conversation prompts based on areas where partners diverged
 * 
 * Props:
 * - insights: Array of insight objects from the comparison
 * - partnerAName: Name of Partner A
 * - partnerBName: Name of Partner B
 * - relationshipId: For saving discussion notes
 */
const InsightsPanel = ({ insights = [], partnerAName = 'Partner A', partnerBName = 'Partner B', relationshipId = null }) => {
  const [expandedInsight, setExpandedInsight] = useState(null);

  if (!insights || insights.length === 0) {
    return null;
  }

  // Get summary insight (first one)
  const summaryInsight = insights.find(i => i.type === 'overview' || i.type === 'celebration');
  const actionableInsights = insights.filter(i => i.type !== 'overview' && i.type !== 'celebration');

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'medium':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'info':
        return 'bg-stone-50 border-stone-200 text-stone-800';
      default:
        return 'bg-stone-50 border-stone-200 text-stone-700';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
      case 'medium':
        return <Lightbulb className="w-5 h-5 text-blue-600" />;
      case 'info':
        return <Heart className="w-5 h-5 text-stone-600" />;
      default:
        return <MessageCircle className="w-5 h-5 text-stone-600" />;
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-8 mb-8" data-testid="insights-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-stone-900">Conversation Insights</h3>
          <p className="text-sm text-stone-600">Prompts to help explore your differences together</p>
        </div>
      </div>

      {/* Summary Card */}
      {summaryInsight && (
        <div className={`rounded-xl border p-6 mb-6 ${summaryInsight.type === 'celebration' ? 'bg-mc-blue-pale border-[var(--mc-border-blue)]' : 'bg-mc-cream border-[var(--mc-border)]'}`}>
          <div className="flex items-start gap-3">
            {summaryInsight.type === 'celebration' ? (
              <Heart className="w-6 h-6 text-mc-blue-deep flex-shrink-0 mt-1" />
            ) : (
              <Lightbulb className="w-6 h-6 text-mc-blue-deep flex-shrink-0 mt-1" />
            )}
            <div>
              <p className="text-stone-900 font-medium mb-3">{summaryInsight.message}</p>
              {summaryInsight.conversation_prompts && (
                <ul className="space-y-2">
                  {summaryInsight.conversation_prompts.map((prompt, idx) => (
                    <li key={idx} className="text-sm text-stone-700 flex items-start gap-2">
                      <span className="text-mc-blue-deep mt-1">•</span>
                      {prompt}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actionable Insights */}
      {actionableInsights.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-semibold text-stone-900 flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Areas for Discussion ({actionableInsights.length})
          </h4>
          
          {actionableInsights.map((insight, idx) => (
            <div 
              key={idx}
              className={`rounded-xl border transition-all ${getPriorityStyles(insight.priority)}`}
            >
              {/* Header - Always visible */}
              <button
                onClick={() => setExpandedInsight(expandedInsight === idx ? null : idx)}
                className="w-full p-4 flex items-center justify-between text-left"
                data-testid={`insight-toggle-${idx}`}
              >
                <div className="flex items-center gap-3">
                  {getPriorityIcon(insight.priority)}
                  <div>
                    <span className="font-semibold">{insight.category}</span>
                    <span className="text-sm ml-2 opacity-75">
                      ({insight.divergence_count} difference{insight.divergence_count !== 1 ? 's' : ''})
                    </span>
                  </div>
                </div>
                {expandedInsight === idx ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {/* Expanded Content */}
              {expandedInsight === idx && (
                <div className="px-4 pb-4 border-t border-opacity-20">
                  {/* Topics that diverged */}
                  {insight.topics && insight.topics.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <p className="text-sm font-medium opacity-80">Where you differed:</p>
                      {insight.topics.map((topic, tidx) => (
                        <div key={tidx} className="bg-white bg-opacity-50 rounded-lg p-3 text-sm">
                          <p className="font-medium mb-2 text-stone-800">{topic.question}</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-blue-50 bg-opacity-50 rounded p-2">
                              <span className="text-xs font-medium text-blue-700 block mb-1">{partnerAName}</span>
                              <span className="text-stone-700">
                                {typeof topic.partner_a === 'string' ? topic.partner_a : JSON.stringify(topic.partner_a)}
                              </span>
                            </div>
                            <div className="bg-purple-50 bg-opacity-50 rounded p-2">
                              <span className="text-xs font-medium text-purple-700 block mb-1">{partnerBName}</span>
                              <span className="text-stone-700">
                                {typeof topic.partner_b === 'string' ? topic.partner_b : JSON.stringify(topic.partner_b)}
                              </span>
                            </div>
                          </div>
                          {topic.specific_prompts && topic.specific_prompts.length > 0 && (
                            <p className="mt-2 text-xs italic text-stone-600">
                              💬 {topic.specific_prompts[0]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Conversation prompts */}
                  {insight.conversation_prompts && insight.conversation_prompts.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium opacity-80 mb-2">Start the conversation:</p>
                      <ul className="space-y-2">
                        {insight.conversation_prompts.map((prompt, pidx) => (
                          <li key={pidx} className="flex items-start gap-2 text-sm">
                            <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-60" />
                            <span>{prompt}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Discussion Notes for this category */}
                  {relationshipId && (
                    <div className="mt-4 pt-4 border-t border-opacity-20">
                      <DiscussionNotes 
                        relationshipId={relationshipId} 
                        category={insight.category}
                        compact={true}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* All Notes Section */}
      {relationshipId && (
        <div className="mt-6">
          <DiscussionNotes relationshipId={relationshipId} />
        </div>
      )}

      {/* Tips Section */}
      <div className="mt-6 p-4 bg-stone-50 rounded-xl">
        <p className="text-sm text-stone-600">
          <strong>💡 Tip:</strong> These prompts work best when you're both calm and have time to talk. 
          There's no rush—pick one topic and explore it together without trying to "solve" anything.
        </p>
      </div>
    </div>
  );
};

export default InsightsPanel;
